/**
 * FORM DI CAMBIO PASSWORD — condiviso fra la tab Sicurezza delle impostazioni
 * (cambio volontario) e la pagina di cambio obbligatorio imposta da Liferay
 * (TS-119). Campi, validazione e chiamata al backend vivono qui e in nessun
 * altro posto: le due schermate differiscono solo per la cornice.
 *
 * La password corrente è richiesta anche nel cambio obbligatorio: il backend la
 * verifica comunque (POST /user/updatePassword), e l'utente l'ha appena digitata
 * per entrare.
 *
 * Terza schermata (FILO-70): la reimpostazione dal link ricevuto per email. Lì
 * la password corrente non c'è — è proprio quella dimenticata — e la prova
 * d'identità è il ticket del link: con la prop `ticket` il campo sparisce e la
 * chiamata va a /user/setPasswordByTicket.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { changeRequiredPassword, setPasswordByTicket, updatePassword } from '../auth/authSlice';
import type { PasswordResetTicket } from '../auth/types';
import { useToast } from './useToast';

interface PasswordChangeFormProps {
  /**
   * Invocata dopo un cambio andato a buon fine. La pagina di cambio
   * obbligatorio la usa per proseguire verso la destinazione originale; la tab
   * impostazioni non la passa (resta dov'è).
   */
  onSuccess?: () => void;
  /**
   * Bottone a tutta larghezza. Serve nella pagina di cambio obbligatorio, dove
   * il form è una colonna stretta in stile login; nella tab impostazioni il
   * bottone mantiene la larghezza naturale come gli altri pannelli.
   */
  fullWidthSubmit?: boolean;
  /**
   * Cambio IMPOSTO da Liferay (flag `passwordReset`). Cambia l'endpoint, non la
   * UI: con il flag alzato ogni chiamata autenticata riceve 403, quindi il
   * cambio deve passare dall'endpoint anonimo. Default: cambio volontario.
   */
  required?: boolean;
  /**
   * Reimpostazione dal link ricevuto per email (FILO-70): niente password
   * corrente, il ticket la sostituisce. Ha la precedenza su `required`.
   */
  ticket?: PasswordResetTicket;
}

export function PasswordChangeForm({
  onSuccess,
  fullWidthSubmit = false,
  required = false,
  ticket,
}: PasswordChangeFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  // Servono solo al cambio imposto: l'endpoint anonimo non ha un utente di
  // contesto, quindi deve ricevere istanza e login di chi sta cambiando.
  const { webId, user } = useAppSelector((state) => state.auth);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  // confirm === next implica anche confirm non vuoto quando next non lo è
  const canSave = (ticket !== undefined || current.length > 0) && next.length > 0 &&
    confirm === next && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      const credentials = {
        currentPassword: current,
        password1: next,
        password2: confirm,
      };
      await (ticket
        ? dispatch(setPasswordByTicket({ ...ticket, password1: next, password2: confirm }))
        : required
          ? dispatch(changeRequiredPassword({
              ...credentials,
              webId: webId ?? '',
              emailAddress: user?.emailAddress ?? '',
            }))
          : dispatch(updatePassword(credentials))
      ).unwrap();
      setCurrent('');
      setNext('');
      setConfirm('');
      // Con il ticket l'esito lo dice la pagina di reimpostazione, che sostituisce il form.
      if (!ticket) toast({ severity: 'success', message: t('common.saved') });
      onSuccess?.();
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {!ticket && (
        <TextField
          type="password"
          label={t('userSettings.security.currentPassword')}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          fullWidth
          className="password-form__field"
          autoComplete="current-password"
        />
      )}
      <TextField
        type="password"
        label={t('userSettings.security.newPassword')}
        value={next}
        onChange={(e) => setNext(e.target.value)}
        fullWidth
        className="password-form__field"
        autoComplete="new-password"
      />
      <TextField
        type="password"
        label={t('userSettings.security.confirmPassword')}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        fullWidth
        className="password-form__field"
        autoComplete="new-password"
        error={mismatch}
        helperText={mismatch ? t('userSettings.security.mismatch') : undefined}
      />

      <div className="password-form__actions">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          fullWidth={fullWidthSubmit}
        >
          {t('common.save')}
        </Button>
      </div>
    </>
  );
}
